import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { components } from "@oathera/contract/api";
import type { Service } from "@oathera/contract";
import { StatusBadge } from "../../components/index.ts";
import { KYA_FLOW, serviceLabel, statusLevel, statusText } from "./vocab.ts";
import { IdpIcon, AisIcon, VciIcon, GatewayIcon, IdentityMcpIcon, ResourceIcon, HealthPulseIcon } from "./metric-icons.tsx";
import { type ReactNode as IconNode } from "react";

/** Per-service decorative icon (IDP/AIS/VCI get bespoke marks; others have none). */
function serviceIcon(service: Service): IconNode | null {
  switch (service) {
    case "idp":
      return <IdpIcon />;
    case "ais":
      return <AisIcon />;
    case "vci":
      return <VciIcon />;
    case "gateway-mcp":
      return <GatewayIcon />;
    case "identity-mcp":
      return <IdentityMcpIcon />;
    case "resource":
      return <ResourceIcon />;
    default:
      return null;
  }
}

/**
 * The KYA service map (design 12.2 Overview row): the five flow components — IDP, AIS, VCI, Gateway,
 * Resource Server — each shown with its health as an icon+text status (never colour alone), and
 * drilling down to its service page. Health comes from the overview response's `services`; a node
 * the response does not carry is shown as an honest grey "unknown" (design 12.3), not green.
 *
 * Services the response reports that are NOT part of the headline KYA flow (`identity-mcp`, `sim`)
 * are listed below the map so nothing the server reports is dropped, but they are not part of the
 * five-node flow line.
 */

type ServiceHealth = components["schemas"]["ServiceHealth"];

export interface ServiceMapProps {
  /** Health per service from the overview response. Empty while loading or when none reported. */
  readonly services: readonly ServiceHealth[];
}

export function ServiceMap(props: ServiceMapProps): ReactNode {
  const byService = new Map<Service, ServiceHealth>();
  for (const s of props.services) byService.set(s.service, s);

  // Any reported service that is not one of the five headline KYA nodes (so the operator still sees
  // it). KYA nodes are rendered from the fixed flow list regardless of whether the response has them.
  const kyaSet = new Set<Service>(KYA_FLOW.map((n) => n.service));
  const extras = props.services.filter((s) => !kyaSet.has(s.service));

  return (
    <section className="obs-panel ov-map" aria-label="KYA service map">
      <h2 className="ov-section__title">KYA flow</h2>
      {/* A 3-column grid: row 1 = IDP, AIS, VCI; row 2 = Gateway, Identity MCP, Resource Server.
          Each cell stretches to fill its column so the nodes are evenly distributed across the
          panel width. */}
      <ul className="ov-map__grid">
        {KYA_FLOW.map((node) => (
          <li key={node.service} className="ov-map__cell">
            <ServiceNode label={node.label} service={node.service} health={byService.get(node.service)} />
          </li>
        ))}
      </ul>
      {extras.length > 0 && (
        <ul className="ov-map__extras" aria-label="Other reported services">
          {extras.map((s) => (
            <li key={s.service} className="ov-map__item">
              <ServiceNode label={serviceLabel(s.service)} service={s.service} health={s} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ServiceNode(props: {
  label: string;
  service: Service;
  health: ServiceHealth | undefined;
}): ReactNode {
  const navigate = useNavigate();
  const { health } = props;

  // No reported health → honest unknown (grey, never green). With health, map the contract status.
  const level = health === undefined ? "unknown" : statusLevel(health.status);
  const statusLabel = health === undefined ? "No data" : statusText(health.status);

  const detail =
    health === undefined
      ? `${props.label}: no health reported`
      : `${props.label}: ${statusLabel.toLowerCase()}` +
        (health.instances_silent > 0
          ? `, ${health.instances_silent} of ${health.instances_total} instance(s) silent`
          : `, ${health.instances_total} instance(s)`);

  const go = (): void => navigate(`/services/${props.service}`);

  return (
    <button
      type="button"
      className="ov-node"
      onClick={go}
      // The number behind the status drills down to the service page (design 12.3).
      aria-label={`${detail}. Open the ${props.label} service page.`}
    >
      <span className="ov-node__label">{props.label}</span>
      <span className="ov-node__status-row">
        <StatusBadge level={level} label={statusLabel} title={detail} size="sm" />
        {level === "healthy" && (
          <span className="ov-node__pulse" aria-hidden="true">
            <HealthPulseIcon service={props.service} />
          </span>
        )}
      </span>
      {serviceIcon(props.service) !== null && (
        <span className="ov-node__icon" aria-hidden="true">{serviceIcon(props.service)}</span>
      )}
      {health !== undefined && health.stale && (
        <span className="ov-node__flag" title="Data for this service is stale (a source is silent).">
          stale
        </span>
      )}
    </button>
  );
}
